import { QuestionType } from '@dadohr/shared/domainTypes/experience';
import {
  ScaleQuestion,
  scaleRange
} from '@dadohr/shared/domainTypes/questionScale';
import styled from '@emotion/styled';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core';
import React from 'react';

const Row = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: p.theme.spacing(1),
  overflowX: 'auto',
  paddingBottom: p.theme.spacing(0.5)
}));

const EndLabel = styled(Typography)((p) => ({
  flex: '0 0 auto',
  maxWidth: 140,
  paddingBottom: p.theme.spacing(1.25),
  color: p.theme.palette.text.secondary
}));

const NumberLabel = styled(FormControlLabel)({
  '&&': {
    margin: 0,
    minWidth: 40
  }
});

/**
 * Google-Forms style numbered scale: a row of radios labelled min..max with the
 * optional end labels on either side. Used by the admin preview (readOnly) and
 * the employee app. Rating questions render the same row for now; the star
 * widget replaces it in the Rating phase.
 */
export const LinearScaleInput = ({
  question,
  value,
  onChange,
  name,
  id,
  disabled,
  readOnly
}: {
  question: ScaleQuestion;
  value: number | null;
  onChange?: (next: number) => void;
  name: string;
  id?: string;
  disabled?: boolean;
  readOnly?: boolean;
}) => {
  const { min, max } = scaleRange(question);
  const values: number[] = [];
  for (let v = min; v <= max; v++) {
    values.push(v);
  }
  const minLabel =
    question.type === QuestionType.LINEAR_SCALE ? question.minLabel : undefined;
  const maxLabel =
    question.type === QuestionType.LINEAR_SCALE ? question.maxLabel : undefined;

  return (
    <Row>
      {minLabel && (
        <EndLabel variant="caption" align="right">
          {minLabel}
        </EndLabel>
      )}
      <RadioGroup
        row
        id={id}
        name={name}
        value={value === null ? '' : String(value)}
        onChange={(ev) => {
          if (readOnly || !onChange) {
            return;
          }
          const next = Number(ev.target.value);
          if (Number.isInteger(next)) {
            onChange(next);
          }
        }}
      >
        {values.map((v) => (
          <NumberLabel
            key={v}
            value={String(v)}
            labelPlacement="top"
            disabled={disabled}
            label={<Typography variant="body2">{v}</Typography>}
            control={<Radio color="primary" disabled={disabled} />}
          />
        ))}
      </RadioGroup>
      {maxLabel && <EndLabel variant="caption">{maxLabel}</EndLabel>}
    </Row>
  );
};
